import compact from 'lodash/compact';
import { ReactElement, useMemo } from 'react';

import { TranslatableText } from '@dreryk/react-common';

import { protectFeature } from '~libs.shared.system';

import { nfzLoginTranslationKeys, personalDataTranslationKeys, personnelTranslationKeys } from '../translations';

import { CertificatePanel } from './certificate/CertificatePanel';
import { LoginSettingsPanel } from './loginSettings/LoginSettingsPanel';
import { NfzLoginPanel } from './nfzLogin/NfzLoginPanel';
import { PersonalDataForm } from './personalData/form/PersonalDataForm';
import { PersonalDataFormLoader } from './personalData/form/PersonalDataForm.loader';

const MY_ACCOUNT_PATH = '/moje-konto';
export const getMyAccountPath = (): typeof MY_ACCOUNT_PATH => MY_ACCOUNT_PATH;

export enum MyAccountPanelNavParams {
   PERSONAL_DATA = 'dane-osobowe',
   LOGIN_SETTINGS = 'ustawienia-logowania',
   NFZ_LOGIN = 'logowanie-nfz',
   CERTIFICATE = 'certyfikat',
}

type MyAccountInternalPath<PATH extends MyAccountPanelNavParams> = `${ReturnType<typeof getMyAccountPath>}/${PATH}`;

export const getNfzLoginPath = (): MyAccountInternalPath<MyAccountPanelNavParams.NFZ_LOGIN> =>
   `${getMyAccountPath()}/${MyAccountPanelNavParams.NFZ_LOGIN}` as const;
export const getPersonalDataPath = (): MyAccountInternalPath<MyAccountPanelNavParams.PERSONAL_DATA> =>
   `${getMyAccountPath()}/${MyAccountPanelNavParams.PERSONAL_DATA}` as const;
export const getCertificatePath = (): MyAccountInternalPath<MyAccountPanelNavParams.CERTIFICATE> =>
   `${getMyAccountPath()}/${MyAccountPanelNavParams.CERTIFICATE}` as const;
export const getLoginSettingsPath = (): MyAccountInternalPath<MyAccountPanelNavParams.LOGIN_SETTINGS> =>
   `${getMyAccountPath()}/${MyAccountPanelNavParams.LOGIN_SETTINGS}` as const;

type MyAccountPanelTabsOptions = {
   onUpdatePersonalData: VoidFunction;
   onCertificateSave: VoidFunction;
};

type MyAccountNavTab = {
   param: MyAccountPanelNavParams;
   label: TranslatableText;
   content: ReactElement;
};

export const useMyAccountPanelTabs = (options: MyAccountPanelTabsOptions): MyAccountNavTab[] => {
   return useMemo(
      () =>
         compact([
            {
               param: MyAccountPanelNavParams.PERSONAL_DATA,
               label: personalDataTranslationKeys.personalData,
               content: (
                  <PersonalDataFormLoader>
                     {({ practitionerDetails, doctorAddress }) => (
                        <PersonalDataForm
                           practitionerDetails={practitionerDetails}
                           onUpdatePersonalData={options.onUpdatePersonalData}
                           doctorAddress={doctorAddress}
                        />
                     )}
                  </PersonalDataFormLoader>
               ),
            },
            {
               param: MyAccountPanelNavParams.LOGIN_SETTINGS,
               label: personnelTranslationKeys.myAccount.loginSettings,
               content: <LoginSettingsPanel />,
            },
            {
               param: MyAccountPanelNavParams.NFZ_LOGIN,
               label: nfzLoginTranslationKeys.nfzLogIn,
               content: <NfzLoginPanel />,
            },
            protectFeature('updatingDoctorPersonalData', {
               param: MyAccountPanelNavParams.CERTIFICATE,
               label: personnelTranslationKeys.myAccount.certificate.certificate,
               content: <CertificatePanel onCertificateSave={options.onCertificateSave} />,
            }),
         ]),
      [options.onUpdatePersonalData, options.onCertificateSave]
   );
};
